import { memo, useState, useRef, useEffect } from 'react';
import * as Y from 'yjs';
import { Flex, Text, Icon, icon } from '@nula/core-ui';
import { clsx } from 'clsx';
import { motion, AnimatePresence } from 'motion/react';
import type { NodeProps, Node } from '@xyflow/react';
import { useReactFlow } from '@xyflow/react';
import styles from './component.module.css';
import type { FrameNodeData } from './types';
import { makeDefault } from './defaults';
import { withDefaults } from '../with-defaults';
import { useActiveFrameDropTarget } from '../../hooks/use-frame-drop-target';
import { SelectionTransformer } from '@/features/canvas/engine/selection/selection-transformer';
import { useNodeActions } from "@/features/canvas/engine/wiring/contexts/node-actions-context";
import { useCanvasDoc } from "@/features/canvas/engine/wiring/contexts/canvas-doc-context";
import { useCanvasBinding } from "@/features/canvas/engine/wiring/contexts/canvas-binding-context";
import { useCanvasReadOnly } from "@/features/canvas/engine/wiring/contexts/canvas-read-only-context";
import { canvasRoots } from "@/features/canvas/engine/wiring/canvas-roots";
import { resolveNodeAriaLabel } from '../../a11y/aria-labels';
import { useParentLabelResolver } from '../../a11y/parent-label-context';
import { getNodeLocked } from '../node-data';
import { subscribeEditTitle } from './edit-title-signal';

type FrameNodeShape = Node<FrameNodeData, 'frame'>;

/**
 * Collaborative frame node. Frames group other nodes via React Flow's
 * parentId + extent: 'parent' contract; their children render translated
 * relative to the frame. The frame component itself does NOT render its
 * children — React Flow handles the spatial nesting in its node tree.
 *
 * Raw-HTML exception: React Flow v12 expects <div> containers for custom
 * nodes. Title/body content uses core-ui primitives.
 */
export default memo(function FrameNode(props: NodeProps<FrameNodeShape>) {
  const { id, data, selected, dragging, parentId } = props;
  const actions = useNodeActions();
  const doc = useCanvasDoc();
  const readOnly = useCanvasReadOnly();
  const binding = useCanvasBinding();
  const { fitView } = useReactFlow();
  // Merge canonical defaults so any missing field falls back to the
  // design-system value rather than rendering as undefined CSS.
  const frameData = withDefaults<FrameNodeData>(data, makeDefault as () => FrameNodeData);
  const activeTarget = useActiveFrameDropTarget();
  const isDropTarget = activeTarget === id;
  const parentLabel = useParentLabelResolver();
  const isLocked = getNodeLocked(data);

  // CSS custom properties carry user-controlled colors / styles (hex +
  // enum values from FrameData) — no inline style objects with mixed
  // keys, only `--*` passthroughs.
  //
  // --frame-bg / --frame-stroke / --frame-stroke-style / --frame-stroke-dasharray
  // flow through to both the expanded body and the collapsed folder so
  // the picked colors + dash pattern apply in either state. The SVG path
  // can't use border-style — it uses stroke-dasharray, so we precompute
  // the dasharray value here from strokeStyle and pass it as a separate
  // CSS var the SVG consumes.
  const strokeDasharray =
    frameData.strokeStyle === 'dashed' ? '6 4' :
    frameData.strokeStyle === 'dotted' ? '2 4' :
    'none';
  // Legacy frames stored fill as rgba(r,g,b,a) with alpha already baked in.
  // color-mix() cannot reliably extract alpha from an rgba string — detect and
  // pass opacity=1 so the stored color renders verbatim. New frames store an
  // opaque hex + a separate fillOpacity so the slider works correctly.
  const fillIsRgba = typeof frameData.fill === 'string' && frameData.fill.startsWith('rgba(');
  const cssVars = {
    // 'none' means no fill — map to transparent so color-mix in CSS doesn't receive
    // the CSS keyword 'none' which is not a valid color for color-mix().
    '--frame-bg': frameData.fill === 'none' ? 'transparent' : frameData.fill,
    '--frame-fill-opacity': frameData.fill === 'none' ? 0 : fillIsRgba ? 1 : frameData.fillOpacity,
    '--frame-stroke': frameData.stroke,
    '--frame-stroke-opacity': frameData.strokeOpacity,
    '--frame-stroke-style': frameData.strokeStyle,
    '--frame-stroke-dasharray': strokeDasharray,
    '--frame-title-color': frameData.titleColor,
  } as React.CSSProperties;

  // a11y per UI checklist + S10 §17 uniform shape: "frame: <title>" with the
  // optional "(in <parent>)" suffix when nested. Empty titles fall back to
  // "Untitled frame" so screen readers announce the landmark instead of a void.
  const ariaLabel =
    frameData.title.trim() === ''
      ? 'Untitled frame'
      : resolveNodeAriaLabel(
          { type: 'frame', data: { title: frameData.title }, parentId: parentId ?? null },
          parentLabel,
        );

  // Inline title editing — double-click the title bar to switch to an
  // <input>; Enter or blur commits, Escape cancels. Locked frames don't
  // enter edit mode.
  const [editingTitle, setEditingTitle] = useState(false);
  const [draftTitle, setDraftTitle] = useState(frameData.title);
  const inputRef = useRef<HTMLInputElement>(null);

  // Sync the draft when the live title changes from outside (collaborator
  // edit, undo) — but only when not actively editing, so we don't clobber
  // the user's in-progress typing.
  useEffect(() => {
    if (!editingTitle) setDraftTitle(frameData.title);
  }, [frameData.title, editingTitle]);

  // Auto-focus + select the entire title when entering edit mode so the
  // user can start typing immediately (replace) or arrow-key to position.
  useEffect(() => {
    if (editingTitle && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editingTitle]);

  const writeTitle = (value: string) => {
    if (!doc) return;
    const node = canvasRoots(doc, binding).nodes.get(id);
    const fdata = node?.get('data');
    if (!(fdata instanceof Y.Map)) return;
    doc.transact(() => fdata.set('title', value), doc.clientID);
  };

  const startEditingTitle = () => {
    if (isLocked || readOnly) return;
    setDraftTitle(frameData.title);
    setEditingTitle(true);
  };

  // Toolbar's "Edit title" button fires through editTitleSignal — same
  // entry point as a double-click on the title bar. Subscribe per nodeId
  // so each frame only reacts to its own request.
  useEffect(() => {
    return subscribeEditTitle(id, () => startEditingTitle());
    // startEditingTitle reads frameData.title + isLocked through closure;
    // re-subscribing on every change would churn the listener set, so
    // intentionally keep the subscription stable for the node's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  const commitTitle = () => {
    if (draftTitle !== frameData.title) writeTitle(draftTitle);
    setEditingTitle(false);
  };
  const cancelTitle = () => {
    setDraftTitle(frameData.title);
    setEditingTitle(false);
  };
  const onTitleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); commitTitle(); }
    else if (e.key === 'Escape') { e.preventDefault(); cancelTitle(); }
  };

  // Click on the collapsed box reopens the frame (writes data.collapsed=false).
  // The frame's NodeFloatingToolbar also exposes a toggle, but click-to-expand
  // is the primary affordance — the closed box looks self-evidently clickable.
  const expandFrame = () => {
    if (!doc || isLocked) return;
    const node = canvasRoots(doc, binding).nodes.get(id);
    const fdata = node?.get('data');
    if (!(fdata instanceof Y.Map)) return;
    doc.transact(() => fdata.set('collapsed', false), doc.clientID);
    // Fit the expanded frame into view after RF re-measures it. The 200ms
    // delay lets the AnimatePresence transition complete and RF recompute
    // the node's new dimensions before fitView runs.
    setTimeout(() => {
      fitView({ nodes: [{ id }], duration: 300, padding: 0.15 });
    }, 200);
  };

  // Double-click on the open frame's empty body collapses it. RF doesn't
  // DOM-nest a frame's children — they render as DOM siblings — so a
  // dblclick that originates on .body cannot have come from a child node
  // or edge. We still guard on `e.target === e.currentTarget` to ignore
  // bubbling from anything that does end up inside the body (overlays,
  // resize handles, etc.), and the title bar has its own onDoubleClick
  // that stopPropagation-s before this can ever fire.
  const collapseFrame = () => {
    if (!doc || isLocked) return;
    const node = canvasRoots(doc, binding).nodes.get(id);
    const fdata = node?.get('data');
    if (!(fdata instanceof Y.Map)) return;
    doc.transact(() => fdata.set('collapsed', true), doc.clientID);
  };

  return (
    <SelectionTransformer
      nodeId={id}
      nodeType="frame"
      selected={!!selected}
      dragging={!!dragging}
      doc={doc ?? undefined}
      onExpand={actions?.onExpand}
    >
      {/* React Flow node container — see engine/README §Raw-HTML exception */}
      <div
        data-testid="frame-node-root"
        data-node-id={id}
        role="group"
        aria-label={ariaLabel}
        aria-disabled={isLocked ? true : undefined}
        data-drop-target={isDropTarget ? 'true' : 'false'}
        data-collapsed={frameData.collapsed ? 'true' : 'false'}
        data-selected={selected || undefined}
        className={clsx(styles.root)}
        style={cssVars}
      >
        {/* AnimatePresence CROSSFADES the two states (no `mode="wait"`) —
            the new branch fades+scales in while the old fades+scales out,
            both occupying the same absolute position inside .root so there
            is no "blank beat" between them. The collapsed branch and the
            expanded wrapper both use position:absolute / inset:0 so the
            size jump driven by RF's inline width/height is masked by the
            crossfade. Easing matches a single curve across both branches
            so the pair reads as one motion. */}
        <AnimatePresence initial={false}>
          {frameData.collapsed ? (
            <motion.button
              key="collapsed"
              type="button"
              // `nopan` prevents the canvas from pan-panning under a
              // mouse-down on the folder, but `nodrag` is intentionally
              // omitted so RF can still initiate a node drag from the
              // collapsed surface. Without this, the user couldn't drag
              // a closed frame around the canvas — the only way to move
              // it would be to reopen it first.
              className={`${styles.collapsedBox} nopan`}
              data-testid="frame-collapsed-box"
              aria-label={`Open frame${frameData.title ? `: ${frameData.title}` : ''}`}
              // Single-click selects the frame (lets RF handle selection so
              // the toolbar appears); double-click reopens. The toolbar's
              // Open/Close toggle is the alternative path. Matches the
              // double-click-to-edit pattern other nodes use.
              onDoubleClick={(e) => { e.stopPropagation(); expandFrame(); }}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.94 }}
              // Slightly longer + softer ease than easeOut. The collapse
              // is a state change the user is watching for, so a 220ms
              // cubic feels considered without feeling slow. cubic-bezier
              // .2/.0/.2/1 is a Material "standard" curve (gentle in/out).
              transition={{ duration: 0.22, ease: [0.2, 0, 0.2, 1] }}
            >
              {/* Folder silhouette traced as a single SVG path so the tab
                  + body outline reads as one continuous shape (not two
                  stacked rectangles). 100×80 viewBox; the body fills
                  most of the frame and the tab rises above the top-left
                  third. preserveAspectRatio=none stretches to fill the
                  collapsed box exactly. */}
              <svg
                viewBox="0 0 100 80"
                preserveAspectRatio="none"
                className={styles.folderSvg}
                aria-hidden="true"
              >
                <path
                  className={styles.folderShape}
                  d="
                    M 4 12
                    Q 4 6 10 6
                    L 36 6
                    Q 40 6 42 10
                    L 44 14
                    L 90 14
                    Q 96 14 96 20
                    L 96 70
                    Q 96 76 90 76
                    L 10 76
                    Q 4 76 4 70
                    Z
                  "
                />
              </svg>
              <Icon name={frameData.icon} size={icon.lg} className={styles.collapsedIcon} />
              {editingTitle ? (
                <input
                  ref={inputRef}
                  className={`${styles.collapsedTitleInput} nopan nodrag`}
                  value={draftTitle}
                  onChange={(e) => setDraftTitle(e.target.value)}
                  onBlur={commitTitle}
                  onKeyDown={onTitleInputKeyDown}
                  aria-label="Frame title"
                  placeholder="Untitled"
                />
              ) : (
                <Text size="xs" className={styles.collapsedTitle}>
                  {frameData.title || 'Untitled'}
                </Text>
              )}
            </motion.button>
          ) : (
            <motion.div
              key="expanded"
              data-testid="frame-expanded-body"
              // Real positioned box (NOT display:contents) so opacity +
              // scale tween predictably and so the expanded body
              // overlays the collapsed folder during crossfade. inset:0
              // makes it fill .root regardless of the current RF
              // dimensions; the title-bar's absolute positioning is
              // measured relative to .root which is unchanged by this
              // wrapper.
              className={styles.expandedWrap}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.22, ease: [0.2, 0, 0.2, 1] }}
            >
              <Flex
                component="header"
                align="center"
                gap="sm"
                className={styles.titleBar}
                data-testid="frame-title-bar"
                onDoubleClick={(e) => { e.stopPropagation(); startEditingTitle(); }}
              >
                {editingTitle ? (
                  <input
                    ref={inputRef}
                    className={`${styles.titleInput} nopan nodrag`}
                    value={draftTitle}
                    onChange={(e) => setDraftTitle(e.target.value)}
                    onBlur={commitTitle}
                    onKeyDown={onTitleInputKeyDown}
                    aria-label="Frame title"
                    placeholder="Untitled frame"
                  />
                ) : (
                  <Text size="sm" strong className={styles.titleText}>
                    {frameData.title || 'Untitled frame'}
                  </Text>
                )}
              </Flex>
              <div
                className={styles.body}
                data-testid="frame-body"
                onDoubleClick={(e) => {
                  // Only collapse when the dblclick lands on the body
                  // itself, not on a descendant. This is defensive — RF
                  // doesn't DOM-nest children — but it costs nothing and
                  // keeps any future overlay rendered inside .body from
                  // triggering an accidental collapse.
                  if (e.target !== e.currentTarget) return;
                  e.stopPropagation();
                  collapseFrame();
                }}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </SelectionTransformer>
  );
});
